import {
  React,
  NavLink,
  useLocation,
  logo,
  MessagesSquare,
  Sparkles,
  Cable,
  Globe,
  useState,
} from '../../utils/globalImports';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExtendedAccountResponse } from '../../store/types';
import { useTranslation } from 'react-i18next';
import { Files, FileUp, Menu } from 'lucide-react';
import CustomClientSelectDialog from '../dialogbox/CustomClientSelectDialog';

interface Props {
  visible: boolean;
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

const VaultiScanSidebar: React.FC<Props> = ({ visible, collapsed = false, onCollapseToggle }) => {
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const userRole = (account?.user?.role || account?.role)?.trim().toLowerCase();
  const userEmail = (account?.user?.email || account?.email || '').toLowerCase();
  const customerId = account?.user?.customer_id || account?.customer_id;
  const clientId = account?.user?.client_id || account?.client_id;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientSelectionTarget, setClientSelectionTarget] = useState<'document-upload' | 'vaulticrawl' | null>(null);

  const handleConfirm = (clientId: string) => {
    setDialogOpen(false);
    const basePath = clientSelectionTarget === 'vaulticrawl' ? '/vaulticrawl' : '/document-upload';
    setClientSelectionTarget(null);
    navigate(`${basePath}/${clientId}`);
  };

  const subMenuItems = [
    {
      name: t('menuItems.chat'),
      path: '/chat',
      icon: MessagesSquare,
    },
    {
      name: t('menuItems.documents'),
      path: '/document-dashboard',
      icon: Files,
      allowedRoles: ['superadmin', 'customeradmin', 'clientadmin'],
    },
    {
      name: t('document.upload'),
      path: '/document-upload',
      icon: FileUp,
      allowedRoles: ['customeradmin', 'clientadmin'],
      exact: false,
    },
    {
      name: t('menuItems.webCrawl'),
      path: '/vaulticrawl',
      icon: Globe,
      allowedRoles: ['superadmin', 'customeradmin', 'clientadmin'],
    },
    {
      name: t('menuItems.agentSidebar'),
      path: '/agents/agent-management',
      icon: Sparkles,
      allowedRoles: ['superadmin'],
    },
    {
      name: t('connectors.customSidebarTitle'),
      path: '/connectors/connector-management',
      icon: Cable,
      allowedRoles: ['superadmin'],
      allowedEmailDomains: ['@vaultiscan.ai'],
    },
  ];

  const filteredMenuItems = subMenuItems.filter((item) => {
    if (item.allowedRoles && userRole) {
      const hasRole = item.allowedRoles.some((r) => r.toLowerCase() === userRole);
      if (!hasRole) return false;
    }

    if (item.allowedEmailDomains && item.allowedEmailDomains.length > 0) {
      const hasAllowedDomain = item.allowedEmailDomains.some((domain) => userEmail.endsWith(domain.toLowerCase()));
      if (!hasAllowedDomain) return false;
    }

    return true;
  });

  return (
    <div className={`customer-sidebar ${visible ? 'slide-in' : 'slide-out'}${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-logo-container" style={{ paddingBottom: '16px' }}>
        {!collapsed && <img src={logo} alt="Logo" className="sidebar-logo" />}
        <button type="button" className="sidebar-collapse-btn" onClick={onCollapseToggle}>
          <Menu size={20} />
        </button>
      </div>

      {filteredMenuItems.map((item) => {
        let to = item.path;
        let onClick: ((event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void) | undefined;

        if (item.name === t('document.upload')) {
          if (userRole === 'clientadmin' && clientId) {
            to = `/document-upload/${clientId}`;
          } else if (userRole === 'customeradmin') {
            to = '/document-upload';
            onClick = (event) => {
              event.preventDefault();
              setClientSelectionTarget('document-upload');
              setDialogOpen(true);
            };
          }
        }

        if (item.path === '/vaulticrawl') {
          if (userRole === 'clientadmin' && clientId) {
            to = `/vaulticrawl/${clientId}`;
          } else if (userRole === 'customeradmin') {
            to = '/vaulticrawl';
            onClick = (event) => {
              event.preventDefault();
              setClientSelectionTarget('vaulticrawl');
              setDialogOpen(true);
            };
          }
        }

        return (
          <div key={item.name} className="sidebar-item">
            <div className="sidebar-link-with-toggle">
              <NavLink
                to={to}
                onClick={onClick}
                end={item.exact}
                className={({ isActive }) => (isActive ? 'sub-active-link' : '')}
              >
                <div className="navlink-content">
                  <item.icon className="sub-sidebar-icon" />
                  {!collapsed && <span>{item.name}</span>}
                </div>
              </NavLink>
            </div>
          </div>
        );
      })}

      <CustomClientSelectDialog
        open={dialogOpen}
        handleClose={() => {
          setDialogOpen(false);
          setClientSelectionTarget(null);
        }}
        handleConfirm={handleConfirm}
        customerId={customerId || ''}
        dialogTitle={
          clientSelectionTarget === 'vaulticrawl'
            ? t('webCrawl.selectClientDialogboxTitle')
            : t('document.selectClientDialogboxTitle')
        }
      />
    </div>
  );
};

export default VaultiScanSidebar;
